import React, { useState, useEffect, useCallback } from 'react';
import { 
    Ticket, 
    ExternalLink, 
    AlertCircle, 
    Loader2,
    Calendar,
    User
} from 'lucide-react';
import AppendixWrapper from './common/AppendixWrapper';
import { useAppendix } from '../context/AppendixContext';

const ZohoDeskTicketsAppendix = ({ email, isOpen, onClose, authFetch }) => {
    const { registerAppendix, setAppendixStatus } = useAppendix();
    const id = 'zoho-tickets';
    
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        registerAppendix(id, {
            title: 'Tickets Zoho Desk',
            icon: Ticket
        });
    }, [registerAppendix]);

    // Sync external isOpen prop with internal appendix status
    useEffect(() => {
        setAppendixStatus(id, isOpen);
    }, [isOpen, setAppendixStatus]);

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await authFetch(`/api/zoho/tickets?email=${encodeURIComponent(email)}`);
            if (response.ok) {
                const data = await response.json();
                setTickets(data.tickets || []);
                setError('Error al cargar tickets');
            }
        } catch (err) {
            console.error('Error fetching tickets:', err);
            setError('Error al cargar tickets');
        } finally {
            setLoading(false);
        }
    }, [email, authFetch]);

    useEffect(() => {
        if (isOpen && email) {
            fetchTickets();
        }
    }, [isOpen, email, fetchTickets]);

    return (
        <AppendixWrapper 
            id={id} 
            title="Tickets Zoho Desk" 
            icon={Ticket}
            className="w-96"
            onClose={onClose}
            defaultPosition={{ x: window.innerWidth - 420, y: 100 }}
        >
            <div className="flex flex-col h-full bg-slate-900">
                {/* Sub-Header with email */}
                <div className="px-4 py-2 bg-slate-800/50 border-b border-slate-700">
                    <p className="text-[10px] font-mono text-slate-400 truncate uppercase tracking-widest">{email || 'Sin email'}</p>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent max-h-[600px]">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-400">
                            <Loader2 className="animate-spin" size={32} />
                            <p className="text-sm">Buscando tickets...</p>
                        </div>
                    ) : error ? (
                        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3 text-red-400">
                            <AlertCircle size={18} className="shrink-0 mt-0.5" />
                            <p className="text-xs">{error}</p>
                        </div>
                    ) : tickets.length === 0 ? (
                        <div className="text-center py-20 space-y-2">
                            <Ticket size={40} className="mx-auto text-slate-700" />
                            <p className="text-slate-400 text-sm">No hay tickets para este email</p>
                        </div>
                    ) : (
                        tickets.map((ticket) => (
                            <div 
                                key={ticket.id} 
                                onClick={() => window.open(ticket.webUrl, '_blank')}
                                className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 hover:border-blue-500/50 hover:bg-slate-800/60 transition-all group cursor-pointer relative"
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <span className="text-[10px] font-mono text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded uppercase tracking-tighter">
                                        #{ticket.ticketNumber}
                                    </span>
                                    <div 
                                        className="text-slate-500 group-hover:text-blue-400 transition-colors"
                                        title="Ver en Zoho Desk"
                                    >
                                        <ExternalLink size={14} />
                                    </div>
                                </div>

                                <h4 className="text-sm font-medium text-slate-200 mb-3 line-clamp-2 leading-snug group-hover:text-white transition-colors">
                                    {ticket.subject}
                                </h4>

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-1.5 text-[10px] text-slate-500 uppercase font-bold tracking-tighter">
                                            <span>Estado</span>
                                        </div>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full inline-block font-bold uppercase tracking-widest ${
                                            ticket.statusType === 'Open' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 
                                            'bg-slate-500/10 text-slate-400 border border-slate-500/20'
                                        }`}>
                                            {ticket.status}
                                        </span>
                                    </div>

                                    <div className="space-y-1 text-right">
                                        <div className="flex items-center justify-end gap-1.5 text-[10px] text-slate-500 uppercase font-bold tracking-tighter">
                                            <span>Prioridad</span>
                                        </div>
                                        <span className={`text-[10px] font-black uppercase tracking-widest ${
                                            ticket.priority === 'High' ? 'text-orange-400' : 
                                            ticket.priority === 'Medium' ? 'text-yellow-400' : 'text-slate-400'
                                        }`}>
                                            {ticket.priority || 'Normal'}
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-4 pt-3 border-t border-slate-700/50 flex items-center justify-between text-[10px] text-slate-500 font-mono">
                                    <div className="flex items-center gap-1.5">
                                        <Calendar size={12} />
                                        <span>{new Date(ticket.createdTime).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <User size={12} />
                                        <span>ID: {ticket.contactId?.slice(-6)}</span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 bg-slate-800/30 border-t border-slate-700 text-center">
                    <button 
                        onClick={fetchTickets}
                        disabled={loading}
                        className="text-[10px] text-blue-400 hover:text-blue-300 font-bold uppercase tracking-widest transition-colors flex items-center gap-2 mx-auto disabled:opacity-50"
                    >
                        <Loader2 size={12} className={loading ? "animate-spin" : ""} />
                        Actualizar listado
                    </button>
                </div>
            </div>
        </AppendixWrapper>
    );
};

export default ZohoDeskTicketsAppendix;
